import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import api from "../../lib/api";
import { AdminHeader, Btn, Card, Input, Textarea, Localized } from "../../components/admin/Fields";
import ImageUpload from "../../components/admin/ImageUpload";

/* Simple 0-100 slider input for opacity settings */
const Slider = ({ label, value, onChange, testId, hint }) => {
  const v = typeof value === "number" ? value : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-[0.7rem] tracking-[0.28em] uppercase text-brand-navy font-medium">{label}</label>
        <span className="text-xs text-brand-medium tabular-nums" data-testid={`${testId}-value`}>{v}</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={v}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#22346B]"
        data-testid={testId}
      />
      {hint ? <p className="text-xs text-brand-medium mt-1">{hint}</p> : null}
    </div>
  );
};

const DEFAULT_MEDIA_CATEGORIES = [
  { key: "news",        label: "Haberler" },
  { key: "video",       label: "Video" },
  { key: "event",       label: "Etkinlik" },
  { key: "blog",        label: "Blog" },
  { key: "press",       label: "Basında Biz" },
  { key: "publication", label: "Yayınlar" },
];

const KEY_HINT = {
  news:        "Haber içeriklerinin göründüğü hikaye halkası",
  video:       "Video içerikleri için hikaye halkası",
  event:       "Etkinlik / Zirve / Fuar içerikleri için",
  blog:        "Blog yazıları için",
  press:       "Basında yer alan haberler için",
  publication: "Raporlar, yayınlar ve dokümanlar için",
};

const R2MigrationCard = () => {
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const run = async () => {
    if (running) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/admin/storage/migrate-to-r2");
      setResult({ ok: true, ...r.data });
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  };
  return (
    <Card title="Depolama — Cloudflare R2 Migration">
      <p className="text-sm text-brand-medium mb-4">
        Sitenizdeki eski Emergent Object Storage&apos;da tutulan tüm görsel/video/PDF dosyalarını Cloudflare R2 CDN&apos;e taşır. İşlem <strong>güvenlidir</strong> ve <strong>tekrar tekrar çalıştırılabilir</strong> — sadece henüz taşınmamış olanlar kopyalanır.
      </p>
      <button
        type="button"
        onClick={run}
        disabled={running}
        className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-primary disabled:opacity-60 text-white px-6 py-3 text-[0.7rem] tracking-[0.28em] uppercase font-medium transition-colors"
        data-testid="r2-migrate-btn"
      >
        {running ? "Migration çalışıyor…" : "Migration'ı Başlat"}
      </button>
      {result ? (
        <div className="mt-6 p-4 border border-black/10 bg-brand-light text-sm" data-testid="r2-migrate-result">
          {result.ok ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-brand-navy">
              <div><div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium">Taranan</div><div className="mt-1 font-serif text-2xl">{result.scanned}</div></div>
              <div><div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium">Yeni Taşınan</div><div className="mt-1 font-serif text-2xl">{result.migrated}</div></div>
              <div><div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium">Zaten R2&apos;de</div><div className="mt-1 font-serif text-2xl">{result.already}</div></div>
              <div><div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium">Bulunamayan</div><div className="mt-1 font-serif text-2xl">{result.missing}</div></div>
              <div><div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium">Hata</div><div className="mt-1 font-serif text-2xl">{result.failed}</div></div>
            </div>
          ) : (
            <div className="text-red-700">Hata: {result.error}</div>
          )}
        </div>
      ) : null}
    </Card>
  );
};

const SEOCard = ({ s, set }) => {
  const socials = Array.isArray(s.seo_social_links) ? s.seo_social_links : [];
  const priorityPages = Array.isArray(s.seo_priority_pages) ? s.seo_priority_pages : [];
  const updateSocial = (i, v) => set("seo_social_links", socials.map((x, idx) => idx === i ? v : x));
  const addSocial = () => set("seo_social_links", [...socials, ""]);
  const rmSocial = (i) => set("seo_social_links", socials.filter((_, idx) => idx !== i));
  const updatePage = (i, field, v) => set("seo_priority_pages", priorityPages.map((p, idx) => idx === i ? { ...p, [field]: v } : p));
  const addPage = () => set("seo_priority_pages", [...priorityPages, { path: "/", title: "", description: "" }]);
  const rmPage = (i) => set("seo_priority_pages", priorityPages.filter((_, idx) => idx !== i));

  return (
    <Card title="SEO — Google Arama Zenginleştirmesi">
      <p className="text-sm text-brand-medium mb-6">
        Bu alanlar Google&apos;a sitenizin kim olduğunu, ne yaptığını ve hangi sayfaların önemli olduğunu anlatır. Arama sonuçlarında <strong>logo</strong>, <strong>şirket adı</strong>, <strong>açıklama</strong> ve <strong>alt-sayfa bağlantıları</strong> (Ekibimiz, İletişim, Projeler…) görünmesini sağlar. Değişikliklerin arama sonuçlarına yansıması genelde <strong>1-4 hafta</strong> sürer.
      </p>

      <div className="space-y-6">
        <Localized
          label="Kurum Kısa Açıklaması (schema.org)"
          value={s.seo_org_description || {}}
          onChange={(v) => set("seo_org_description", v)}
          textarea
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Kuruluş Yılı" type="number" value={s.seo_founding_year || 0} onChange={(v) => set("seo_founding_year", Number(v) || 0)} testId="seo-founding" />
          <Input label="Kurucu / CEO Adı" value={s.seo_founder_name || ""} onChange={(v) => set("seo_founder_name", v)} testId="seo-founder" />
          <Input label="Telefon (uluslararası format)" value={s.seo_contact_phone || ""} onChange={(v) => set("seo_contact_phone", v)} placeholder="+902125978575" testId="seo-phone" />
          <Input label="E-posta" value={s.seo_contact_email || ""} onChange={(v) => set("seo_contact_email", v)} placeholder="info@firatconstruction.com" testId="seo-email" />
          <Input label="Adres — Sokak" value={s.seo_address_street || ""} onChange={(v) => set("seo_address_street", v)} testId="seo-street" />
          <Input label="Adres — Şehir" value={s.seo_address_city || ""} onChange={(v) => set("seo_address_city", v)} placeholder="İstanbul" testId="seo-city" />
          <Input label="Adres — İl / Bölge" value={s.seo_address_region || ""} onChange={(v) => set("seo_address_region", v)} testId="seo-region" />
          <Input label="Adres — Posta Kodu" value={s.seo_address_postal || ""} onChange={(v) => set("seo_address_postal", v)} testId="seo-postal" />
        </div>
      </div>

      <div className="mt-8 border-t border-black/10 pt-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-brand-navy font-medium">Sosyal Medya Profilleri</div>
            <div className="text-xs text-brand-medium">Facebook, Instagram, LinkedIn, YouTube, X/Twitter — arama sonucunda logo altında sosyal simgeler</div>
          </div>
          <button type="button" onClick={addSocial} className="text-xs tracking-[0.24em] uppercase text-brand-navy hover:text-brand-primary" data-testid="add-social">+ URL Ekle</button>
        </div>
        <div className="space-y-2">
          {socials.map((v, i) => (
            <div key={i} className="flex gap-2" data-testid={`social-row-${i}`}>
              <Input value={v} onChange={(nv) => updateSocial(i, nv)} placeholder="https://www.linkedin.com/company/…" />
              <button type="button" onClick={() => rmSocial(i)} className="px-3 text-xs text-red-600 hover:text-red-700 border border-red-200 hover:border-red-400 transition-colors" data-testid={`rm-social-${i}`}>Sil</button>
            </div>
          ))}
          {!socials.length ? <div className="text-xs text-brand-medium">Henüz sosyal medya bağlantısı eklenmemiş.</div> : null}
        </div>
      </div>

      <div className="mt-8 border-t border-black/10 pt-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={s.seo_sitelink_search !== false}
            onChange={(e) => set("seo_sitelink_search", e.target.checked)}
            data-testid="seo-sitelink-search"
          />
          <span className="text-sm text-brand-navy">Google&apos;da Site İçi Arama Kutusu Göster (Sitelinks Search Box)</span>
        </label>
      </div>

      <div className="mt-8 border-t border-black/10 pt-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-brand-navy font-medium">Öncelikli Sayfalar (Sitelinks Aday Listesi)</div>
            <div className="text-xs text-brand-medium">Bu sayfalar arama sonucunun altında bağlantı olarak görünme adayıdır (Ekibimiz, İletişim, Projeler…). Google nihai kararı verir ama listede olması şansı ciddi artırır.</div>
          </div>
          <button type="button" onClick={addPage} className="text-xs tracking-[0.24em] uppercase text-brand-navy hover:text-brand-primary" data-testid="add-priority-page">+ Sayfa Ekle</button>
        </div>
        <div className="space-y-3">
          {priorityPages.map((p, i) => (
            <div key={i} className="border border-black/10 p-4 bg-brand-light" data-testid={`priority-page-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input label="Yol (path)" value={p.path || ""} onChange={(v) => updatePage(i, "path", v)} placeholder="/about" />
                <Input label="Başlık" value={p.title || ""} onChange={(v) => updatePage(i, "title", v)} placeholder="Hakkımızda" />
                <div className="flex gap-2 items-end">
                  <Input label="Açıklama" value={p.description || ""} onChange={(v) => updatePage(i, "description", v)} placeholder="Kısa açıklama" />
                  <button type="button" onClick={() => rmPage(i)} className="px-3 py-2 text-xs text-red-600 hover:text-red-700 border border-red-200 hover:border-red-400 transition-colors" data-testid={`rm-priority-page-${i}`}>Sil</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};



const MediaCategoriesCard = ({ items, onChange }) => {
  const list = items && items.length ? items : DEFAULT_MEDIA_CATEGORIES;
  const update = (key, value) => {
    const next = list.map((it) => it.key === key ? { ...it, label: value } : it);
    onChange(next);
  };
  const resetAll = () => onChange(DEFAULT_MEDIA_CATEGORIES);
  return (
    <Card
      title="Medya Merkezi — Kategori Etiketleri"
      action={
        <button
          type="button"
          onClick={resetAll}
          className="text-xs tracking-[0.24em] uppercase text-brand-medium hover:text-brand-navy transition-colors"
          data-testid="media-cats-reset"
        >
          Varsayılana Dön
        </button>
      }
    >
      <p className="text-sm text-brand-medium mb-4">
        Ana sayfa ve <strong>/media</strong> hikaye halkalarında görünen etiketleri düzenleyin. Anahtar (<code>key</code>) sabittir — içeriklerinizle eşleşir. Yalnızca <strong>Etiket</strong> alanını değiştirin. (Örn: &quot;Zirve&quot; → &quot;Etkinlik&quot;)
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.map((c) => (
          <div key={c.key} className="border border-black/10 p-4 bg-white" data-testid={`media-cat-row-${c.key}`}>
            <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-medium mb-2">
              Anahtar · <code className="text-brand-navy font-medium">{c.key}</code>
            </div>
            <Input
              label="Görünen Etiket"
              value={c.label}
              onChange={(v) => update(c.key, v)}
              testId={`media-cat-input-${c.key}`}
              placeholder={DEFAULT_MEDIA_CATEGORIES.find((x) => x.key === c.key)?.label}
            />
            {KEY_HINT[c.key] ? (
              <p className="text-xs text-brand-medium mt-2">{KEY_HINT[c.key]}</p>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
};

export const RealEstateCard = ({ s, set }) => {
  const [ok, setOk] = useState({ opps: true, adv: true, faq: true, stats: true, bullets: true });
  const parseInto = (key, val, flag) => {
    try {
      const parsed = JSON.parse(val);
      set(key, parsed);
      setOk((o) => ({ ...o, [flag]: true }));
    } catch {
      setOk((o) => ({ ...o, [flag]: false }));
    }
  };
  return (
    <Card title="Landing Page — Yenişehir / Arnavutköy Arsa Yatırımı">
      <p className="text-sm text-brand-medium mb-5">
        <strong>/realestate</strong> sayfasını Google Ads trafiği için özel olarak yönetin. Kartlar, avantajlar, SSS ve iletişim bilgileri buradan güncellenir.
      </p>

      <label className="flex items-center gap-3 cursor-pointer mb-6">
        <input type="checkbox" checked={s.real_estate_enabled !== false} onChange={(e) => set("real_estate_enabled", e.target.checked)} data-testid="re-enabled" />
        <span className="text-sm"><strong>Landing sayfası aktif</strong> (kapatırsanız /realestate erişilemez olur)</span>
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Hero Üst Etiket (Kicker)" value={s.real_estate_hero_kicker || ""} onChange={(v) => set("real_estate_hero_kicker", v)} testId="re-kicker" placeholder="YENİŞEHİR / ARNAVUTKÖY" />
        <Input label="Sayfa Telefonu (ör. +90 555 111 22 33)" value={s.real_estate_phone || ""} onChange={(v) => set("real_estate_phone", v)} testId="re-phone" />
        <Input label="WhatsApp (sadece rakam, ör. 905551112233)" value={s.real_estate_whatsapp || ""} onChange={(v) => set("real_estate_whatsapp", v)} testId="re-wa" />
        <Input label="Google Maps Embed src" value={s.real_estate_map_embed || ""} onChange={(v) => set("real_estate_map_embed", v)} testId="re-map" placeholder="https://www.google.com/maps/embed?..." />
      </div>

      <div className="mt-4"><Input label="Hero Başlık" value={s.real_estate_hero_title || ""} onChange={(v) => set("real_estate_hero_title", v)} testId="re-title" /></div>
      <div className="mt-4"><Textarea label="Hero Alt Metin" rows={3} value={s.real_estate_hero_subtitle || ""} onChange={(v) => set("real_estate_hero_subtitle", v)} testId="re-subtitle" /></div>

      <div className="mt-6">
        <ImageUpload label="Hero Arka Plan Görseli" value={s.real_estate_hero_image} onChange={(v) => set("real_estate_hero_image", v)} testId="re-hero-image" />
        <p className="text-xs text-brand-medium mt-2">2000×1200 px veya daha büyük tavsiye edilir.</p>
      </div>

      <div className="mt-8 pt-6 border-t border-black/10">
        <div className="text-brand-navy font-medium mb-3">Hero Değer Önerileri (bullets)</div>
        <Textarea rows={5} value={JSON.stringify(s.real_estate_hero_bullets || [], null, 2)} onChange={(v) => parseInto("real_estate_hero_bullets", v, "bullets")} testId="re-bullets" />
        {!ok.bullets ? <p className="text-xs text-red-600 mt-1">JSON geçerli değil.</p> : null}
      </div>

      <div className="mt-6">
        <div className="text-brand-navy font-medium mb-3">Güven İstatistikleri (JSON: [{"{"} value, label {"}"}, …])</div>
        <Textarea rows={6} value={JSON.stringify(s.real_estate_trust_stats || [], null, 2)} onChange={(v) => parseInto("real_estate_trust_stats", v, "stats")} testId="re-stats" />
        {!ok.stats ? <p className="text-xs text-red-600 mt-1">JSON geçerli değil.</p> : null}
      </div>

      <div className="mt-6">
        <div className="text-brand-navy font-medium mb-3">Avantajlar (JSON: [{"{"} title, description {"}"}, …])</div>
        <Textarea rows={8} value={JSON.stringify(s.real_estate_advantages || [], null, 2)} onChange={(v) => parseInto("real_estate_advantages", v, "adv")} testId="re-advantages" />
        {!ok.adv ? <p className="text-xs text-red-600 mt-1">JSON geçerli değil.</p> : null}
      </div>

      <div className="mt-6">
        <div className="text-brand-navy font-medium mb-3">Arsa Fırsatları (JSON: title, location, size, price, badge, description, image, features[])</div>
        <Textarea rows={16} value={JSON.stringify(s.real_estate_opportunities || [], null, 2)} onChange={(v) => parseInto("real_estate_opportunities", v, "opps")} testId="re-opps" />
        {!ok.opps ? <p className="text-xs text-red-600 mt-1">JSON geçerli değil.</p> : null}
        <p className="text-xs text-brand-medium mt-2"><code>image</code> alanına <strong>/uploads/…</strong> veya tam URL yazabilirsiniz. Boş bırakırsanız otomatik stok görsel kullanılır.</p>
      </div>

      <div className="mt-6">
        <div className="text-brand-navy font-medium mb-3">Sık Sorulan Sorular (JSON: [{"{"} q, a {"}"}, …])</div>
        <Textarea rows={10} value={JSON.stringify(s.real_estate_faq || [], null, 2)} onChange={(v) => parseInto("real_estate_faq", v, "faq")} testId="re-faq" />
        {!ok.faq ? <p className="text-xs text-red-600 mt-1">JSON geçerli değil.</p> : null}
      </div>

      <div className="mt-10 pt-8 border-t border-black/10">
        <div className="text-brand-navy font-serif text-xl mb-1">A/B Hero Testi</div>
        <p className="text-xs text-brand-medium mb-4">
          Açıldığında ziyaretçiler %50/%50 rastgele olarak Varyant A (yukarıdaki varsayılan hero) veya Varyant B&apos;yi görür.
          Her lead hangi varyanttan geldiğini kaydeder — Arsa Yatırımı Talepleri sayfasında dönüşüm karşılaştırmasını görürsünüz.
          Test için <code>?variant=A</code> veya <code>?variant=B</code> parametresi ile zorlayabilirsiniz.
        </p>

        <label className="flex items-center gap-3 cursor-pointer mb-6">
          <input type="checkbox" checked={s.real_estate_ab_enabled === true} onChange={(e) => set("real_estate_ab_enabled", e.target.checked)} data-testid="re-ab-enabled" />
          <span className="text-sm"><strong>A/B testi aktif</strong> (kapalıyken herkes Varyant A&apos;yı görür)</span>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Varyant B — Kicker" value={s.real_estate_hero_kicker_b || ""} onChange={(v) => set("real_estate_hero_kicker_b", v)} testId="re-b-kicker" placeholder="ARNAVUTKÖY YATIRIM FIRSATI" />
          <div /> {/* spacer */}
        </div>
        <div className="mt-4"><Input label="Varyant B — Hero Başlık" value={s.real_estate_hero_title_b || ""} onChange={(v) => set("real_estate_hero_title_b", v)} testId="re-b-title" placeholder="30 Yılda 10 Katına Çıkan Arsalar" /></div>
        <div className="mt-4"><Textarea label="Varyant B — Alt Metin" rows={3} value={s.real_estate_hero_subtitle_b || ""} onChange={(v) => set("real_estate_hero_subtitle_b", v)} testId="re-b-subtitle" /></div>
        <div className="mt-4">
          <ImageUpload label="Varyant B — Hero Görsel" value={s.real_estate_hero_image_b} onChange={(v) => set("real_estate_hero_image_b", v)} testId="re-b-image" />
        </div>
      </div>
    </Card>
  );
};


export const YenisehirCard = ({ s, set }) => {
  const [ok, setOk] = useState({ testimonials: true });
  const parseInto = (key, val, flag) => {
    try { const p = JSON.parse(val); set(key, p); setOk((o) => ({ ...o, [flag]: true })); }
    catch { setOk((o) => ({ ...o, [flag]: false })); }
  };
  // Map markers row-editor
  const markers = Array.isArray(s.yenisehir_map_markers) ? s.yenisehir_map_markers : [];
  const setMarker = (idx, patch) => {
    const next = markers.map((m, i) => (i === idx ? { ...m, ...patch } : m));
    set("yenisehir_map_markers", next);
  };
  const addMarker = () => set("yenisehir_map_markers", [...markers, { label: "", lat: "", lng: "", icon: "◉", link: "" }]);
  const removeMarker = (idx) => set("yenisehir_map_markers", markers.filter((_, i) => i !== idx));
  return (
    <Card title="Yenişehir Premium Landing (/yenisehir)">
      <p className="text-sm text-brand-medium mb-5">Award-winning experience sayfası — cinematic hero, 3D map, timeline. Aşağıdaki alanları doldurarak canlıya alın.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Hero Video URL (YouTube veya MP4)" value={s.yenisehir_hero_video_url || ""} onChange={(v) => set("yenisehir_hero_video_url", v)} testId="yn-hero-video" placeholder="https://youtu.be/... veya https://.../drone.mp4" />
        <div className="text-xs text-brand-medium self-end pb-2">Harita: Leaflet + OpenStreetMap · ücretsiz, token gerekmez ✅</div>
      </div>
      <div className="mt-4 pt-4 border-t border-black/5">
        <div className="text-brand-navy font-medium mb-3">e-iPAT İmar Planı Entegrasyonu</div>
        <div className="grid grid-cols-1 gap-3">
          <Input label="İmar Planı GeoJSON URL (tam, 4303 parsel)" value={s.yenisehir_imar_geojson_url || ""} onChange={(v) => set("yenisehir_imar_geojson_url", v)} testId="yn-imar-url" placeholder="https://.../api/geojson/imar-plani/tam" />
          <Input label="İmar Planı GeoJSON URL (hafif, 1887 parsel)" value={s.yenisehir_imar_geojson_light_url || ""} onChange={(v) => set("yenisehir_imar_geojson_light_url", v)} testId="yn-imar-light-url" placeholder="https://.../api/geojson/imar-plani" />
          <Input label="e-iPAT Deep Link Base URL" value={s.yenisehir_eipat_base_url || ""} onChange={(v) => set("yenisehir_eipat_base_url", v)} testId="yn-eipat-base" placeholder="https://e-ipat.com" />
          <p className="text-xs text-brand-medium">
            İmar planı katmanı Mapbox haritasında canlı olarak çizilir. Ziyaretçi bir parsele tıklayınca detay paneli açılır ve <em>e-iPAT&apos;ta Detaylı Rapor</em> butonu bu URL&apos;e yönlendirir.
          </p>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-black/5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-brand-navy font-medium">Harita Katmanları / Projeler</div>
            <div className="text-xs text-brand-medium">Yenişehir haritasında altın nokta ile göstermek istediğiniz özel projeler ve konumlar</div>
          </div>
          <button type="button" onClick={addMarker} className="text-[0.66rem] tracking-[0.28em] uppercase bg-brand-navy text-white px-4 py-2 hover:bg-brand-primary" data-testid="yn-add-marker">+ Katman Ekle</button>
        </div>
        {markers.length === 0 ? (
          <div className="text-xs text-brand-medium py-6 text-center border border-dashed border-black/15">Henüz katman yok. &quot;+ Katman Ekle&quot; ile başlayın.</div>
        ) : (
          <div className="space-y-3">
            {markers.map((m, i) => (
              <div key={i} className="border border-black/10 p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid={`yn-marker-row-${i}`}>
                <div className="md:col-span-4">
                  <Input label="Başlık (Proje/Yer adı)" value={m.label || ""} onChange={(v) => setMarker(i, { label: v })} testId={`yn-marker-label-${i}`} placeholder="Örn. TOKİ Yenişehir Etap 3" />
                </div>
                <div className="md:col-span-2">
                  <Input label="Enlem (lat)" value={m.lat || ""} onChange={(v) => setMarker(i, { lat: v })} testId={`yn-marker-lat-${i}`} placeholder="41.1650" />
                </div>
                <div className="md:col-span-2">
                  <Input label="Boylam (lng)" value={m.lng || ""} onChange={(v) => setMarker(i, { lng: v })} testId={`yn-marker-lng-${i}`} placeholder="28.6500" />
                </div>
                <div className="md:col-span-1">
                  <Input label="İkon" value={m.icon || ""} onChange={(v) => setMarker(i, { icon: v })} testId={`yn-marker-icon-${i}`} placeholder="◉" />
                </div>
                <div className="md:col-span-2">
                  <Input label="Link (opsiyonel)" value={m.link || ""} onChange={(v) => setMarker(i, { link: v })} testId={`yn-marker-link-${i}`} placeholder="https://..." />
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <button type="button" onClick={() => removeMarker(i)} className="text-red-500 hover:text-red-700 text-[0.66rem] tracking-[0.24em] uppercase" data-testid={`yn-marker-del-${i}`}>Sil</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-3 text-xs text-brand-medium">
          <strong>Koordinat bulmak için:</strong> <a href="https://www.google.com/maps" target="_blank" rel="noreferrer" className="underline">Google Maps</a>&apos;te konuma sağ tıklayın → ilk çıkan sayı (ör. <code>41.1650, 28.6500</code>) — virgülle ayırarak enlem/boylam alanlarına yapıştırın.
        </p>
      </div>
      <div className="mt-4 pt-4 border-t border-black/5">
        <div className="text-brand-navy font-medium mb-3">CEO / Vizyoner Bölümü</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Ad Soyad" value={s.yenisehir_ceo_name || ""} onChange={(v) => set("yenisehir_ceo_name", v)} testId="yn-ceo-name" />
          <Input label="Ünvan" value={s.yenisehir_ceo_title || ""} onChange={(v) => set("yenisehir_ceo_title", v)} testId="yn-ceo-title" />
        </div>
        <div className="mt-4"><Textarea label="Kısa Biyografi" rows={3} value={s.yenisehir_ceo_bio || ""} onChange={(v) => set("yenisehir_ceo_bio", v)} testId="yn-ceo-bio" /></div>
        <div className="mt-4"><ImageUpload label="Portre Görseli" value={s.yenisehir_ceo_portrait} onChange={(v) => set("yenisehir_ceo_portrait", v)} testId="yn-ceo-portrait" /></div>
        <div className="mt-4">
          <ImageUpload
            kind="video"
            label="Portre Video (opsiyonel — sürekli oynayan sessiz döngü, MP4/WebM)"
            value={s.yenisehir_ceo_portrait_video || ""}
            onChange={(v) => set("yenisehir_ceo_portrait_video", v)}
            testId="yn-ceo-portrait-video"
          />
          <div className="mt-1 text-xs text-brand-medium">
            Doldurulursa portre görseli yerine bu video otomatik/sessiz oynar. Önerilen: 8-15sn döngü, 720p MP4, &lt;5&nbsp;MB. Dosya seçtiğinizde otomatik olarak Cloudflare&apos;a yüklenir.
          </div>
        </div>
        <div className="mt-4">
          <ImageUpload
            kind="both"
            label="CEO Mesaj Videosu (YouTube URL yapıştır veya kendi MP4&apos;ünü yükle)"
            value={s.yenisehir_ceo_video_url || ""}
            onChange={(v) => set("yenisehir_ceo_video_url", v)}
            testId="yn-ceo-video"
          />
          <div className="mt-1 text-xs text-brand-medium">
            Tam CEO mesajı için: YouTube linki yapıştırabilir veya kendi MP4&apos;ünüzü yükleyebilirsiniz.
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-black/5">
        <div className="text-brand-navy font-medium mb-3">Yatırımcı Görüşleri (JSON: [{"{"} name, title, quote, portrait, video_url? {"}"}, ...])</div>
        <Textarea rows={10} value={JSON.stringify(s.yenisehir_testimonials || [], null, 2)} onChange={(v) => parseInto("yenisehir_testimonials", v, "testimonials")} testId="yn-testimonials" />
        {!ok.testimonials ? <p className="text-xs text-red-600 mt-1">JSON geçerli değil.</p> : null}
      </div>
    </Card>
  );
};


const AdminSettingsPage = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data || {})); }, []);
  const set = (k, v) => setS((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", s);
      // Wipe the cached copy on the visitor's browser so the next public page
      // fetches fresh settings — no stale logo/theme flash after edits.
      try { localStorage.removeItem("firat.settings.v1"); } catch { /* ignore */ }
      toast.success("Kaydedildi");
    }
    catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };

  if (!s) return <div>…</div>;

  return (
    <div data-testid="admin-settings-page">
      <AdminHeader eyebrow="Site" title="Site Ayarları" action={<Btn onClick={save} disabled={busy} testId="settings-save">Kaydet</Btn>} />
      <div className="space-y-6">
        <Card title="Ana Sayfa Hero Video / Görsel Ayarları">
          <p className="text-sm text-brand-medium mb-4">
            Ana sayfadaki tam ekran hero videosunun üzerindeki karartma, gren (tanecik) ve vinyet katmanlarının yoğunluğunu buradan ayarlayın. <strong>0</strong> katmanı tamamen kaldırır (video &quot;canlı&quot; görünür), <strong>100</strong> ise maksimum karartma yapar. Yazıların okunabilmesi için 30-60 arası önerilir.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Slider label="Perde Karartması (Overlay)" value={s.hero_overlay_opacity} onChange={(v) => set("hero_overlay_opacity", v)} testId="settings-hero-overlay" hint="Videonun üzerindeki lacivert karartma. Videoyu 'canlı' görmek için 20-30 deneyin." />
            <Slider label="Alt Gradyan" value={s.hero_bottom_gradient_opacity} onChange={(v) => set("hero_bottom_gradient_opacity", v)} testId="settings-hero-gradient" hint="Alt kısımda başlığın okunabilmesi için karartma. Genelde 30-50 arası." />
            <Slider label="Vinyet (Köşe Karartması)" value={s.hero_vignette_opacity} onChange={(v) => set("hero_vignette_opacity", v)} testId="settings-hero-vignette" hint="Sinematik köşe karartma. Kapatmak için 0." />
            <Slider label="Gren / Tanecik (Grain)" value={s.hero_grain_opacity} onChange={(v) => set("hero_grain_opacity", v)} testId="settings-hero-grain" hint="Analog film tanecik dokusu. Modern görünüm için 0-10 deneyin." />
          </div>
        </Card>

        <MediaCategoriesCard
          items={s.media_categories || []}
          onChange={(v) => set("media_categories", v)}
        />

        <R2MigrationCard />

        {/* NOTE: RealEstateCard + YenisehirCard removed here (2026-02) —
             they now live on their own /admin/landing-pages screen. */}

        <SEOCard s={s} set={set} />

        <Card title="Analytics & Reklam & Destek — Çerez Onayına Bağlı">
          <p className="text-sm text-brand-medium mb-4">
            Bu kimlikleri girin, sitede otomatik olarak yüklenir. <strong>Ancak</strong> her script yalnızca ziyaretçi çerez onay bandından ilgili kategoriye <strong>&quot;Kabul&quot;</strong> derse çalışır (KVKK & GDPR uyumlu).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Google Analytics 4 (GA4)" value={s.ga4_measurement_id || ""} onChange={(v) => set("ga4_measurement_id", v)} placeholder="G-XXXXXXXXX" testId="ga4-id" />
            <Input label="Google Tag Manager (GTM)" value={s.google_tag_manager_id || ""} onChange={(v) => set("google_tag_manager_id", v)} placeholder="GTM-XXXXXXX" testId="gtm-id" />
            <Input label="Google Ads" value={s.google_ads_id || ""} onChange={(v) => set("google_ads_id", v)} placeholder="AW-XXXXXXXXX" testId="ads-id" />
            <Input label="Meta Pixel (Facebook / Instagram)" value={s.meta_pixel_id || ""} onChange={(v) => set("meta_pixel_id", v)} placeholder="1234567890" testId="meta-id" />
            <Input label="TikTok Pixel" value={s.tiktok_pixel_id || ""} onChange={(v) => set("tiktok_pixel_id", v)} placeholder="C0XXXXXX" testId="tiktok-id" />
            <Input label="LinkedIn Insight Tag" value={s.linkedin_insight_id || ""} onChange={(v) => set("linkedin_insight_id", v)} placeholder="1234567" testId="linkedin-id" />
            <Input label="Hotjar" value={s.hotjar_id || ""} onChange={(v) => set("hotjar_id", v)} placeholder="3456789" testId="hotjar-id" />
          </div>
          <div className="mt-6 pt-6 border-t border-black/10">
            <div className="text-brand-navy font-medium mb-3">Canlı Sohbet & Destek Widget&apos;ları</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="JivoChat Widget ID" value={s.jivochat_id || ""} onChange={(v) => set("jivochat_id", v)} placeholder="aBcD1234EfGh" testId="jivo-id" />
              <Input label="Tawk.to Property ID" value={s.tawk_property_id || ""} onChange={(v) => set("tawk_property_id", v)} placeholder="5f0abcd1234567890" testId="tawk-id" />
            </div>
          </div>
        </Card>

        <Card title="Tarayıcı Sekmesi & Favicon">
          <p className="text-sm text-brand-medium mb-4">
            Tarayıcı sekmesinde görünen başlık ve simge (favicon). Ayrıca alt sayfalarda başlık biçimi de burada belirlenir.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Varsayılan Sekme Başlığı (site_title)"
              value={s.site_title || ""}
              onChange={(v) => set("site_title", v)}
              testId="settings-site-title"
              placeholder="Fırat Construction Yapı A.Ş."
            />
            <Input
              label="Başlık Şablonu (title_template)"
              value={s.title_template || ""}
              onChange={(v) => set("title_template", v)}
              testId="settings-title-template"
              placeholder="%s | Fırat Construction"
            />
          </div>
          <p className="text-xs text-brand-medium mt-2">
            Şablon içindeki <code className="text-brand-navy">%s</code>, o sayfanın kendi SEO başlığı ile değiştirilir. Örn:
            <em> Hakkımızda | Fırat Construction</em>. Boş bırakırsanız yalnızca sayfa başlığı görüntülenir.
          </p>
          <div className="mt-6">
            <ImageUpload
              label="Favicon (Tarayıcı sekmesindeki küçük simge)"
              value={s.favicon_url}
              onChange={(v) => set("favicon_url", v)}
              testId="settings-favicon"
            />
            <p className="text-xs text-brand-medium mt-2">
              Kare oranlı, tercihen 512×512 px PNG veya ICO. Şeffaf zemin önerilir. Değişiklik sonrasında tarayıcının önbelleği nedeniyle yeni simgenin görünmesi 1-2 dakika sürebilir.
            </p>
          </div>
          <div className="mt-6">
            <Input
              label="Public Base URL (sitemap için tam alan adı)"
              value={s.public_base_url || ""}
              onChange={(v) => set("public_base_url", v)}
              testId="settings-public-base-url"
              placeholder="https://firatconstruction.com"
            />
            <p className="text-xs text-brand-medium mt-1">
              /api/public/sitemap.xml dosyasında bu adres kullanılır. Boş bırakırsanız mevcut istek adresi baz alınır.
            </p>
          </div>
        </Card>

        <Card title="Kurumsal Kimlik">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Şirket Adı" value={s.company_name} onChange={(v) => set("company_name", v)} testId="settings-company-name" />
            <Input label="Kısa Ad" value={s.company_short} onChange={(v) => set("company_short", v)} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <ImageUpload
                label="Ana Logo (Header ve Footer'da kullanılır)"
                value={s.logo_url}
                onChange={(v) => set("logo_url", v)}
                testId="settings-logo"
              />
              <p className="text-xs text-brand-medium mt-2">
                Şeffaf zeminli PNG veya SVG önerilir. Yüksekliği en az 200 px olsun (yaklaşık 1000×260).
              </p>
            </div>
            <div>
              <ImageUpload
                label="Alternatif Logo (Koyu zemin için — opsiyonel)"
                value={s.logo_dark_url}
                onChange={(v) => set("logo_dark_url", v)}
                testId="settings-logo-dark"
              />
              <p className="text-xs text-brand-medium mt-2">
                Koyu arka plan üzerinde kullanılacak beyaz/açık logo varyantı. Boş bırakırsanız ana logo kullanılır.
              </p>
            </div>
          </div>
          <label className="flex items-start gap-3 mt-6 cursor-pointer p-3 bg-brand-light/60 border border-black/5" data-testid="settings-auto-whiten-wrap">
            <input
              type="checkbox"
              checked={s.logo_auto_whiten_on_dark !== false}
              onChange={(e) => set("logo_auto_whiten_on_dark", e.target.checked)}
              className="mt-0.5"
              data-testid="settings-auto-whiten"
            />
            <span className="text-sm">
              <strong>Koyu zeminlerde logoyu otomatik beyazlaştır</strong>
              <span className="block text-xs text-brand-medium mt-1">
                Admin paneli sidebar&apos;ı ve giriş sayfasının sol bloğu gibi koyu arka planlarda,
                yüklediğiniz logo koyu renkli ise otomatik olarak beyaza çevrilir. Eğer &quot;Alternatif Logo&quot;
                alanına zaten beyaz/açık renkli bir logo yüklediyseniz bu kutuyu kapatın.
              </span>
            </span>
          </label>
        </Card>

        <Card title="İletişim">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="E-posta" value={s.contact_email} onChange={(v) => set("contact_email", v)} />
            <Input label="Telefon" value={s.contact_phone} onChange={(v) => set("contact_phone", v)} />
          </div>
          <div className="mt-6"><Localized label="Genel Merkez Adres" value={s.hq_address} onChange={(v) => set("hq_address", v)} textarea /></div>
        </Card>

        <Card title="Yüzen İletişim Widget (WhatsApp + Geri Arama)">
          <p className="text-sm text-brand-medium mb-4">Bu bilgiler; her sayfanın sağ alt köşesindeki yüzen iletişim balonunda kullanılır. Sizi arayan potansiyel müşteriler için birebir yönlendirme kanalıdır.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="WhatsApp Numarası (ör: 905551234567 — + veya boşluk yok)" value={s.whatsapp_number} onChange={(v) => set("whatsapp_number", v)} testId="settings-whatsapp" />
            <Input label="WhatsApp Karşılama Mesajı" value={s.whatsapp_message} onChange={(v) => set("whatsapp_message", v)} />
          </div>
          <label className="flex items-center gap-3 mt-4 cursor-pointer">
            <input type="checkbox" checked={s.callback_enabled !== false} onChange={(e) => set("callback_enabled", e.target.checked)} data-testid="settings-callback-enabled" />
            <span className="text-sm">&quot;Sizi Arayalım&quot; (Geri Arama) formu aktif</span>
          </label>
        </Card>

        <Card title="Bitrix24 CRM Formları">
          <p className="text-sm text-brand-medium mb-5">
            Ana sayfa ve İletişim sayfasındaki çoklu-konulu iletişim formu için Bitrix24 inline form yapılandırmaları. &quot;Varsayılan&quot; işaretli olan form sayfa açıldığında seçili gelir. Her form için &quot;Form ID&quot; (ör. <code className="text-brand-navy">inline/78/6madoo</code>) ve &quot;Loader URL&quot; (ör. <code className="text-brand-navy">https://cdn.bitrix24.com.tr/b28402203/crm/form/loader_78.js</code>) girmeniz gerekiyor. Bunları Bitrix24 &gt; CRM &gt; Formlar &gt; ilgili form &gt; embed script&apos;inden alabilirsiniz.
          </p>

          {(s.bitrix_forms || []).map((f, i) => (
            <div key={i} className="border border-black/10 p-5 mb-4" data-testid={`bitrix-row-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="text-[0.68rem] tracking-[0.28em] uppercase text-brand-navy font-medium">
                  Form #{i + 1}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (i === 0) return;
                      const list = [...(s.bitrix_forms || [])];
                      [list[i - 1], list[i]] = [list[i], list[i - 1]];
                      list.forEach((x, k) => (x.order = k + 1));
                      set("bitrix_forms", list);
                    }}
                    className="p-1.5 border border-black/15 hover:border-brand-navy hover:text-brand-navy transition-colors"
                    aria-label="Yukarı"
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const list = [...(s.bitrix_forms || [])];
                      if (i >= list.length - 1) return;
                      [list[i + 1], list[i]] = [list[i], list[i + 1]];
                      list.forEach((x, k) => (x.order = k + 1));
                      set("bitrix_forms", list);
                    }}
                    className="p-1.5 border border-black/15 hover:border-brand-navy hover:text-brand-navy transition-colors"
                    aria-label="Aşağı"
                  >
                    <ArrowDown size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!window.confirm("Bu formu silmek istediğinize emin misiniz?")) return;
                      const list = (s.bitrix_forms || []).filter((_, k) => k !== i);
                      set("bitrix_forms", list);
                    }}
                    className="p-1.5 border border-red-200 text-red-500 hover:bg-red-50"
                    aria-label="Sil"
                    data-testid={`bitrix-delete-${i}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Anahtar (key)"
                  value={f.key || ""}
                  onChange={(v) => {
                    const list = [...(s.bitrix_forms || [])];
                    list[i] = { ...list[i], key: v };
                    set("bitrix_forms", list);
                  }}
                  testId={`bitrix-key-${i}`}
                />
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-3 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="bitrix-default"
                      checked={!!f.is_default}
                      onChange={() => {
                        const list = (s.bitrix_forms || []).map((x, k) => ({ ...x, is_default: k === i }));
                        set("bitrix_forms", list);
                      }}
                      data-testid={`bitrix-default-${i}`}
                    />
                    <span>Varsayılan (ilk açılan)</span>
                  </label>
                </div>
              </div>

              <div className="mt-4">
                <Localized
                  label="Sekme Başlığı (TR / EN / AR)"
                  value={f.label || {}}
                  onChange={(v) => {
                    const list = [...(s.bitrix_forms || [])];
                    list[i] = { ...list[i], label: v };
                    set("bitrix_forms", list);
                  }}
                  testId={`bitrix-label-${i}`}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Input
                  label="Form ID"
                  placeholder="inline/78/6madoo"
                  value={f.form_id || ""}
                  onChange={(v) => {
                    const list = [...(s.bitrix_forms || [])];
                    list[i] = { ...list[i], form_id: v };
                    set("bitrix_forms", list);
                  }}
                  testId={`bitrix-formid-${i}`}
                />
                <Input
                  label="Loader URL"
                  placeholder="https://cdn.bitrix24.com.tr/.../loader_78.js"
                  value={f.loader_url || ""}
                  onChange={(v) => {
                    const list = [...(s.bitrix_forms || [])];
                    list[i] = { ...list[i], loader_url: v };
                    set("bitrix_forms", list);
                  }}
                  testId={`bitrix-loader-${i}`}
                />
              </div>
            </div>
          ))}

          <Btn
            onClick={() => {
              const list = [...(s.bitrix_forms || [])];
              list.push({
                key: `form-${list.length + 1}`,
                label: { tr: "Yeni Konu", en: "New Topic", ar: "موضوع جديد" },
                form_id: "",
                loader_url: "",
                is_default: list.length === 0,
                order: list.length + 1,
              });
              set("bitrix_forms", list);
            }}
            testId="bitrix-add"
          >
            <Plus size={14} /> Yeni Form Ekle
          </Btn>
        </Card>

        <Card title="Ofisler (JSON)">
          <Textarea rows={10} value={JSON.stringify(s.offices || [], null, 2)} onChange={(v) => { try { set("offices", JSON.parse(v)); } catch { /* ignore */ } }} />
        </Card>

        <Card title="Sosyal Medya (JSON)">
          <Textarea rows={5} value={JSON.stringify(s.socials || [], null, 2)} onChange={(v) => { try { set("socials", JSON.parse(v)); } catch { /* ignore */ } }} />
        </Card>

        <Card title="Diller">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Diller (virgülle)" value={(s.languages || []).join(",")} onChange={(v) => set("languages", v.split(",").map((x) => x.trim()).filter(Boolean))} />
            <Input label="Varsayılan Dil" value={s.default_language} onChange={(v) => set("default_language", v)} />
          </div>
        </Card>

        <Card title="Footer">
          <Localized label="Footer Metni" value={s.footer_tagline} onChange={(v) => set("footer_tagline", v)} textarea />
          <div className="mt-6"><Input label="Copyright" value={s.copyright_text} onChange={(v) => set("copyright_text", v)} /></div>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
